// src/utils/MixinAuth.js
import pako from 'pako';
import { v4 as uuidv4 } from 'uuid';
import { getChallenge } from '@/utils/mixin-crypto'; 

/**
 * Mixin 授权工具类
 */
export default class MixinAuth {
  /**
   * 构造函数
   * @param {Object} options - 配置选项
   */
  constructor(options = {}) {
    this.api = options.api || 'https://api.mixin.one';
    this.endpoint = options.endpoint || 'wss://blaze.mixin.one';
    this.handled = false;
    this.ws = null;
    this.useModal = options.useModal || false; // 是否使用模态框而不是直接跳转
  }

  /**
   * 断开 WebSocket 连接
   */
  disconnect() {
    if (this.ws) {
      this.ws.close();
    }
  }

  /**
   * 生成 HTTP 授权 URL
   * @param {String} clientId - 应用的 client_id
   * @param {String} scope - 授权范围
   * @param {String} callbackUrl - 回调 URL
   * @returns {String} 授权 URL
   */
  getOAuthUrl(clientId, scope, callbackUrl) {
    const encodedCallback = encodeURIComponent(callbackUrl);
    const encodedScope = encodeURIComponent(scope);
    return `${this.api}/oauth/authorize?client_id=${clientId}&scope=${encodedScope}&response_type=code&redirect_uri=${encodedCallback}`;
  }

  /**
   * 启动 WebSocket 授权流程
   * @param {String} clientId - 应用的 client_id
   * @param {String} scope - 授权范围
   * @param {Function} callback - 成功回调
   * @param {Function} errorCallback - 错误回调
   * @returns {Object} 当前实例
   */
  authorize(clientId, scope, callback, errorCallback, modalCallback) {
    let redirected = false;
    
    const handleAuthResponse = (response) => {
      const data = response.data;
      
      // 处理错误情况
      if (response && response.error && (response.error.code === 400 || response.error.code === 10002)) {
        errorCallback(response.error);
        return true;
      }
      
      // 检查是否有授权数据
      if (!data) return false;
      
      // 如果有授权码，则调用成功回调
      if (data.authorization_code && data.authorization_code.length > 16) {
        callback(data.authorization_code);
        return true;
      }
      
      // // 如果没有授权码但有 code_id，尝试跳转到 Mixin 应用
      // if (!redirected && data.code_id) {
      //   window.location.href = `mixin://codes/${data.code_id}`;
      //   redirected = true;
      // }
      // 如果没有授权码但有 code_id
      if (!redirected && data.code_id) {
        if (this.useModal && typeof modalCallback === 'function') {
          // 使用模态框显示跳转链接
          modalCallback(data.code_id);
          redirected = true;
        } else {
          // 尝试跳转到 Mixin 应用
          window.location.href = `mixin://codes/${data.code_id}`;
          redirected = true;
        }
      }
      
      return false;
    };

    this.connect(handleAuthResponse, clientId, scope);
    return this;
  }

  /**
   * 建立 WebSocket 连接
   * @param {Function} callback - 消息处理回调
   * @param {String} clientId - 应用的 client_id
   * @param {String} scope - 授权范围
   */
  connect(callback, clientId, scope) {
    const self = this;
    self.handled = false;
    self.callback = callback;

    const { verifier, challenge } = getChallenge();
    self.codeVerifier = verifier;
    self.codeChallenge = challenge;

    // 创建 WebSocket 连接
    const ReconnectingWebSocket = window.ReconnectingWebSocket || require('reconnecting-websocket').default;
    self.ws = new ReconnectingWebSocket(self.endpoint, 'Mixin-OAuth-1', {
      maxReconnectionDelay: 5000,
      minReconnectionDelay: 1000,
      reconnectionDelayGrowFactor: 1.2,
      connectionTimeout: 8000,
      maxRetries: Infinity,
      debug: false,
    });

    // 处理收到的消息
    self.ws.addEventListener('message', function(event) {
      if (self.handled) return;
      
      const fileReader = new FileReader();
      fileReader.onload = function() {
        try {
          // 解压消息
          const msg = pako.ungzip(new Uint8Array(this.result), { to: 'string' });
          const response = JSON.parse(msg);
          
          // 调用回调处理响应
          if (self.callback(response)) {
            self.handled = true;
          } else {
            // 如果未处理完成，继续请求授权码
            setTimeout(function() {
              self.sendRefreshCode(clientId, scope, self.codeChallenge, response.data);
            }, 1000);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('处理 WebSocket 消息时出错:', error);
        }
      };
      
      fileReader.readAsArrayBuffer(event.data);
    });

    // WebSocket 连接建立时发送授权请求   //// 1111
    self.ws.addEventListener('open', function() {
      self.sendRefreshCode(clientId, scope, self.codeChallenge);
    });
  }

  /**
   * 发送刷新授权码请求
   * @param {String} clientId - 应用的 client_id
   * @param {String} scope - 授权范围
   * @param {String} codeChallenge - PKCE 码挑战(可选)
   * @param {Object} authorization - 之前的授权信息
   */
  sendRefreshCode(clientId, scope, codeChallenge = '', authorization = null) {
    if (this.handled) return;
    
    this.send({
      id: uuidv4().toUpperCase(),
      action: 'REFRESH_OAUTH_CODE',
      params: {
        client_id: clientId,
        scope: scope,
        code_challenge: codeChallenge,
        authorization_id: authorization ? authorization.authorization_id : '',
      },
    });
  }

  /**
   * 发送消息到 WebSocket
   * @param {Object} msg - 要发送的消息对象
   */
  send(msg) {
    try {
      this.ws.send(pako.gzip(JSON.stringify(msg)));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('发送 WebSocket 消息时出错:', error);
    }
  }
}